import { FocusControl, View } from "silver";
import { ContextualPopup, ContextualPopupBehavior } from "shared/Components/ContextualPopup";
import { Signal } from "shared/Signal";
import { WindowLayout } from "core/WindowLayout";

type PointOutAction = {
	text: string;
	action: () => any;
	primary?: boolean;
};

interface PointOutViewData {
	title?: string | Node;
	body?: string | Node;
	actions: PointOutAction[];
}

export interface PointOutOptions<T> extends PointOutViewData {
	ref: HTMLElement;
	modifierClass?: string;
	dismissSignal?: Signal<T>;
}

const PointOutHTML = `
	<div class="announcement -pointOut -tooltip">
		<div class="announcement_icon">
			<i class="announcement_bell mycon -bell -white"></i>
		</div>
		<h2 class="announcement_title" data-myon-if="{{ title }}">{{ title @noescape }}</h2>
		<p class="announcement_body" data-myon-if="{{ body }}">{{ body @noescape }}</p>
		<div class="announcement_actions" data-myon-template="actions">
			<button class="button {{ primary? | '-primary' }} {{ !primary? | '-secondary' }}" type="button" data-myon-on-click="handleActionClick">{{ text }}</button>
		</div>
	</div>
`;

class PointOutPopup {
	private view: View<PointOutViewData>;
	public readonly popup: ContextualPopup;

	constructor(
		title: string|Node|undefined,
		body: string|Node|undefined,
		actions: Array<{ text: string, action: () => any, primary?: boolean }> = [],
		modifierClass: string = "-infoNotice"
	) {
		this.view = new View<PointOutViewData>(PointOutHTML, {
			handleActionClick: (e, v: View<{ action: () => any }>) => {
				v.action();
			}
		},  { title, body, actions });

		this.popup = new ContextualPopup(this.view.element, modifierClass);
		this.popup.element.classList.add(modifierClass);
	}
}

export class PointOutService {

	constructor(
		private windowLayout: WindowLayout,
		private focusControl: FocusControl
	) { }

	public async pointTo<T>(options: PointOutOptions<T|null>): Promise<T|null> {
		await this.windowLayout.scrollIntoView(options.ref, 500);

		const signal = options.dismissSignal || new Signal<T|null>();
		const popup = new PointOutPopup(options.title, options.body, options.actions, options.modifierClass);
		popup.popup.setContext(options.ref, ContextualPopupBehavior.NONE);
		popup.popup.show();
		this.focusControl.focusArea(popup.popup.element);

		popup.popup.onHide(() => signal.send(null));

		return new Promise<T|null>(resolve => {
			signal.listen((value) => {
				popup.popup.remove();
				resolve(value);
			});
		});
	}
}
